import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Button,
    Container,
    Grid,
    Tooltip,
    Typography,
} from '@mui/material';
import { BarChart } from '../components/charts/BarChart';
import {
    graphFeatureDescription,
    AudioFeatureResults,
    emptyUserResultData,
    AudioFeatureResultsKeys,
    featureStringMap,
    chooseMostSimilarPerson,
    isFailedToGetUseResults,
} from '../helpers/extractedResults';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { comparisonData } from '../helpers/comparisonData';
import ReactGA from 'react-ga';
import { PauseListChart } from '../components/charts/PauseListChart';
import { externalRoutes } from '../routing/routing';

const InstructorInformation = () => {
    return (
        <Accordion sx={{ justifyContent: 'center', textAlign: 'center' }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    '& .MuiAccordionSummary-content': {
                        justifyContent: 'center',
                    },
                    backgroundColor: '#efe8e1',
                }}
            >
                <Typography sx={{ color: 'primary.dark' }}>
                    Click here to meet the instructors we’re comparing you to.
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    {comparisonData.map((teacher, index) => {
                        return (
                            <Grid
                                key={index}
                                item
                                xs={12}
                                sm={6}
                                sx={{ display: 'flex' }}
                            >
                                <Avatar
                                    alt={teacher.name}
                                    src={teacher.image}
                                    sx={{ width: 56, height: 56 }}
                                />
                                <Container
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography>
                                        {teacher.description}
                                    </Typography>
                                    {teacher.photoCite}
                                </Container>
                            </Grid>
                        );
                    })}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

interface FeatureButtonsProps {
    selectedGraph: string;
    setSelectedGraph: (key: string) => void;
}
const FeatureButtons = ({
    selectedGraph,
    setSelectedGraph,
}: FeatureButtonsProps) => {
    return (
        <Container
            sx={{
                display: 'flex',
                justifyContent: 'center',
                flexFlow: 'wrap',
                marginTop: 2,
            }}
        >
            {Object.entries(graphFeatureDescription).map(([key, value]) => {
                return (
                    <Button
                        key={key}
                        sx={{
                            marginLeft: 2,
                            '&:focus': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                            },
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                            },
                            ...(key === selectedGraph && {
                                color: 'white',
                                backgroundColor: 'primary.main',
                            }),
                        }}
                        onClick={() => setSelectedGraph(key)}
                    >
                        {value.name}
                    </Button>
                );
            })}
        </Container>
    );
};

/**
 * Joins the teacher's names and the user
 *  * @return {Array<string>} labels    for displaying line graphs
 */
const getLineGraphLabels = () => {
    return ['You', ...comparisonData.map((comparison) => comparison.name)];
};

/**
 * Gets Bar Graph Data by joining user data with comparison
 * @param  {string} selectedGraph
 * @param  {boolean} hasComparisonData
 * @param  {boolean} data - The user's graph data
 * @return {{label: string, data: Array<numbers>, backgroundColor: Array<string>}} - Required object to display the chartjs graph
 */
const getBarGraphData = (
    selectedGraph: string,
    hasComparisonData: boolean,
    data: AudioFeatureResults
) => {
    const comparisonDataSet: any = hasComparisonData
        ? comparisonData.map(
            (comparison: any) =>
                comparison[selectedGraph as AudioFeatureResultsKeys] ?? 0
        )
        : [];

    return [
        {
            label: selectedGraph,
            data: [
                data[selectedGraph as AudioFeatureResultsKeys],
                ...comparisonDataSet,
            ], //Gets all the Instructor Data for that graph
            backgroundColor: [
                '#AAD1CB',
                ...comparisonData.map(() => '#BC5D7F'),
            ],
        },
    ];
};

interface ChartProps {
    hasComparisonData: boolean;
    selectedGraph: string;
    tooltipFormatter?: (tooltipItems: any) => string;
    data: AudioFeatureResults;
}

const Chart = ({
    hasComparisonData,
    selectedGraph,
    tooltipFormatter,
    data,
}: ChartProps) => {
    const selectedGraphInfo = graphFeatureDescription[selectedGraph as string];

    if (selectedGraphInfo.graphType === 'bar') {
        return (
            <BarChart
                key={selectedGraph}
                labels={getLineGraphLabels()}
                datasets={getBarGraphData(
                    selectedGraph,
                    hasComparisonData,
                    data
                )}
                tooltipFormatter={tooltipFormatter}
            />
        );
    }
    if (selectedGraphInfo.graphType === 'line') {
        return <PauseListChart datasets={data?.pausesList || [[0, 0]]} />;
    }

    return <></>;
};

interface ResultsPageProps {
    userData: AudioFeatureResults | null | undefined;
    handlePlayAgain: () => void;
    handleFailToGetResults: () => void;
}

export const ResultsPage = ({
    userData,
    handlePlayAgain,
    handleFailToGetResults,
}: ResultsPageProps) => {
    const [selectedGraph, setSelectedGraph] = useState<string>('pitchMedian');
    const [shareTooltip, setShareTooltip] = useState(false);

    useEffect(() => {
        if (!userData || isFailedToGetUseResults(userData))
            handleFailToGetResults();
    }, [handleFailToGetResults, userData]);

    useEffect(() => {
        ReactGA.pageview('TeacherVoice: Results page');
    }, []);

    const data = useMemo(() => {
        return userData ? userData : emptyUserResultData;
    }, [userData]);

    const doesGraphHaveComparisonData = useMemo(() => {
        return comparisonData[0].hasOwnProperty(selectedGraph);
    }, []);

    const similarPerson = chooseMostSimilarPerson(data, comparisonData);

    const title = isFailedToGetUseResults(data)
        ? 'Congrats! Here are your results!'
        : `Congrats! You're ${similarPerson.val}% most like ${similarPerson.name}.`;

    const getFeatureExplanation = () => {
        return graphFeatureDescription[selectedGraph as string];
    };

    const getShareMessage = useCallback(() => {
        const results = data;
        return `Checkout my results! Pitch Mean: ${results.pitchMedian} Hz, Pitch Range: ${results.pitchRange} Hz, Pauses: ${results.pauseRate} pauses per second, Speech Rate: ${results.speechRate} words per second. Get your results today by trying https://nowwithfeeling.com/teacherVoice`;
    }, [data]);

    const handleShare = useCallback(() => {
        ReactGA.event({
            category: 'TeacherVoice',
            action: 'Clicked share',
            label: 'TeacherVoice: Share',
        });

        navigator.clipboard.writeText(getShareMessage());
        setShareTooltip(true);
    }, [setShareTooltip, getShareMessage]);

    const tooltipLabelFormatter = (tooltipItems: any) => {
        return `${featureStringMap[tooltipItems.dataset.label]}: ${tooltipItems.formattedValue
            } ${getFeatureExplanation().units}`;
    };

    return (
        <Grid container spacing={2}>
            <Grid
                sx={{ justifyContent: 'center', textAlign: 'center' }}
                item
                sm={12}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontFamily: "'Berkshire Swash', 'sans-serif'",
                        color: '#587a7f',
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        // fontFamily: "'Berkshire Swash', 'sans-serif'",
                        color: '#587a7f',
                        marginTop: 1,
                        marginBottom: 1.3,
                    }}
                >
                    Checkout your results below
                </Typography>
            </Grid>
            <Grid
                sx={{
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: '100%',
                }}
                item
                sm={12}
            >
                <InstructorInformation />
            </Grid>
            <FeatureButtons
                selectedGraph={selectedGraph}
                setSelectedGraph={setSelectedGraph}
            />
            <Grid item sm={12} sx={{ width: '100%' }}>
                <Container>
                    <Typography variant="h6" sx={{ color: 'primary.dark' }}>
                        {getFeatureExplanation().name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'primary.dark' }}>
                        {getFeatureExplanation().description}
                    </Typography>
                </Container>
                <Chart
                    hasComparisonData={doesGraphHaveComparisonData}
                    selectedGraph={selectedGraph}
                    data={data}
                    tooltipFormatter={tooltipLabelFormatter}
                />
            </Grid>
            <Grid
                sx={{ justifyContent: 'center', textAlign: 'center' }}
                item
                sm={12}
            >
                <Button
                    variant="outlined"
                    onClick={handlePlayAgain}
                    sx={{ marginRight: 2, marginBottom: 1 }}
                >
                    Play Again
                </Button>
                <Button
                    variant="contained"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={externalRoutes.survey}
                    sx={{ marginRight: 2, marginBottom: 1 }}
                >
                    {'Report bugs & feedback'}
                </Button>
                <Tooltip
                    arrow
                    open={shareTooltip}
                    onClose={() => setShareTooltip(false)}
                    title="You've copied your results! Share the url to your friends!"
                >
                    <Button
                        variant="contained"
                        onClick={handleShare}
                        sx={{ marginBottom: 1, backgroundColor: '#bc5d7f' }}
                    >
                        Share your results!
                    </Button>
                </Tooltip>
            </Grid>
        </Grid>
    );
};
